import { z } from 'zod'
import { parseGermanNumber } from '@/lib/utils'

// Echtes Kalenderdatum: OCR kann "2026-02-31" liefern — das <input type=date>
// zeigt es leer an, RHF haelt den Wert aber und ein Regex allein liesse ihn durch.
export function isCalendarDate(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const dt = new Date(Date.UTC(y, mo - 1, d))
  return y >= 2000 && y <= 2100 && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d
}
const optionalDate = z.string().refine((v) => v === '' || isCalendarDate(v), 'Ungültiges Datum').optional()
// Betraege als Text (deutsches Format "1.234,56"), geparst wird beim Speichern
const optionalAmount = z
  .string()
  .trim()
  .refine((v) => v === '' || parseGermanNumber(v) !== null, 'Betrag z.B. 1.234,56')
  .optional()

export const outgoingInvoiceSchema = z
  .object({
    customer_name: z.string().trim().min(1, 'Kunde/Empfänger ist erforderlich').max(200, 'Höchstens 200 Zeichen'),
    invoice_number: z.string().trim().max(100, 'Höchstens 100 Zeichen').optional(),
    invoice_date: z.string().min(1, 'Rechnungsdatum ist erforderlich').refine(isCalendarDate, 'Ungültiges Datum'),
    gross_amount: z
      .string()
      .trim()
      .min(1, 'Rechnungsbetrag ist erforderlich')
      .refine((v) => parseGermanNumber(v) !== null, 'Betrag z.B. 1.234,56')
      .refine((v) => (parseGermanNumber(v) ?? 0) > 0, 'Betrag muss größer als 0 sein')
      .refine((v) => (parseGermanNumber(v) ?? 0) < 100_000_000, 'Betrag ist unplausibel hoch'),
    net_amount: optionalAmount,
    vat_amount: optionalAmount,
    payment_days: z.string().trim().regex(/^\d{0,3}$/, 'Nur ganze Tage').optional(),
    due_date: optionalDate,
    skonto_enabled: z.boolean(),
    // Skonto-Felder bewusst ohne eigene Regeln: sie werden nur geprueft, wenn
    // Skonto angehakt ist (superRefine). Sonst blockiert ein Rest im
    // ausgeblendeten Feld still das Speichern.
    skonto_percent: z.string().optional(),
    skonto_amount: z.string().optional(),
    skonto_date: z.string().optional(),
    description: z.string().trim().max(2000, 'Höchstens 2000 Zeichen').optional(),
  })
  // Gleiche Regeln wie POST /api/invoices/outgoing — laufen sie auseinander,
  // kommt der Fehler erst beim Speichern als Toast statt am Feld.
  .superRefine((v, ctx) => {
    const gross = parseGermanNumber(v.gross_amount)
    for (const key of ['net_amount', 'vat_amount'] as const) {
      const n = parseGermanNumber(v[key])
      if (n !== null && gross !== null && (n < 0 || n > gross)) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'Passt nicht zum Bruttobetrag' })
      }
    }
    if (v.due_date && v.invoice_date && v.due_date < v.invoice_date) {
      ctx.addIssue({ code: 'custom', path: ['due_date'], message: 'Fälligkeit liegt vor dem Rechnungsdatum' })
    }
    if (!v.skonto_enabled) return
    const pct = parseGermanNumber(v.skonto_percent)
    if (pct === null || pct <= 0 || pct >= 100) {
      ctx.addIssue({ code: 'custom', path: ['skonto_percent'], message: 'Skontosatz z.B. 2 oder 3' })
    }
    const amount = parseGermanNumber(v.skonto_amount)
    if (v.skonto_amount?.trim() && amount === null) {
      ctx.addIssue({ code: 'custom', path: ['skonto_amount'], message: 'Betrag z.B. 23,80' })
    } else if (amount !== null && (amount <= 0 || (gross !== null && amount >= gross))) {
      ctx.addIssue({ code: 'custom', path: ['skonto_amount'], message: 'Muss kleiner als der Rechnungsbetrag sein' })
    }
    if (!v.skonto_date) {
      ctx.addIssue({ code: 'custom', path: ['skonto_date'], message: 'Skonto gültig bis ist erforderlich' })
    } else if (!isCalendarDate(v.skonto_date)) {
      ctx.addIssue({ code: 'custom', path: ['skonto_date'], message: 'Ungültiges Datum' })
    } else if (v.invoice_date && v.skonto_date < v.invoice_date) {
      ctx.addIssue({ code: 'custom', path: ['skonto_date'], message: 'Skontofrist liegt vor dem Rechnungsdatum' })
    } else if (v.due_date && v.skonto_date > v.due_date) {
      ctx.addIssue({ code: 'custom', path: ['skonto_date'], message: 'Skontofrist liegt nach der Fälligkeit' })
    }
  })

export type OutgoingInvoiceInput = z.infer<typeof outgoingInvoiceSchema>
